/**
 * Bare PSI wrapper around react-aria-components' `Menu` + `MenuTrigger`.
 * Intended as a building block for higher-level menu wrappers, not for direct
 * use in product code.
 */
import { cx } from 'class-variance-authority';
import {
    Header as AriaHeader,
    Menu as AriaMenu,
    MenuItem as AriaMenuItem,
    type MenuItemProps as AriaMenuItemProps,
    type MenuProps as AriaMenuProps,
    MenuSection as AriaMenuSection,
    MenuTrigger as AriaMenuTrigger,
    type MenuTriggerProps as AriaMenuTriggerProps,
    type SectionProps as AriaSectionProps,
    Separator as AriaSeparator,
} from 'react-aria-components';
import { makeTestID } from 'util/stdtypes';

import styles from './base-menu.module.css';
import { BasePopover, type BasePopoverProps } from './base-popover';

// Re-export primitives so consumers don't need to import from
// react-aria-components directly.
export type { AriaMenuTriggerProps as MenuTriggerProps };
export { AriaMenuTrigger as MenuTrigger };

// `T extends object = object` lets the type of `items` flow into the
// function-child, so `item` gets inferred field types when BaseMenu is called
// with dynamic items, e.g:
//
//     <BaseMenu items={[{ id: 'edit', text: 'Edit' }]}>
//         {({id, text}) => <MenuItemButton id={id} text={text} />}
//     </BaseMenu>
//
// Defaults to `object` for the static-children case, e.g:
//
//     <BaseMenu>
//         <MenuItemButton id='edit' text={useTranslation(editLabel)} />
//         <MenuItemButton
//             id='delete' text={useTranslation(deleteLabel)} destructive
//         />
//     </BaseMenu>
export type BaseMenuProps<T extends object = object> = {
    menuClassName?: string;
    placement?: BasePopoverProps['placement'];
    popoverClassName?: string;
    popoverStyle?: React.CSSProperties;
    header?: React.ReactNode;
} & Pick<AriaMenuProps<T>, 'onAction' | 'selectionMode' | 'selectedKeys' | 'onSelectionChange' | 'disabledKeys' | 'items' | 'children'> &
    Pick<BasePopoverProps, 'isNonModal' | 'isOpen' | 'offset' | 'onOpenChange' | 'triggerRef'>;

export function BaseMenu<T extends object = object>(props: BaseMenuProps<T>) {
    const {
        children,
        placement = 'bottom',
        menuClassName,
        isNonModal,
        isOpen,
        offset,
        onOpenChange,
        triggerRef,
        popoverClassName,
        popoverStyle,
        header,
        ...menuProps
    } = props;

    const menu = (
        <AriaMenu<T> className={cx(styles.menu, menuClassName)} {...menuProps}>
            {children}
        </AriaMenu>
    );

    const content = header ? (
        <div className={styles.menuLayout}>
            <div className={styles.menuHeader}>{header}</div>
            {menu}
        </div>
    ) : (
        menu
    );

    return (
        <BasePopover
            isNonModal={isNonModal}
            isOpen={isOpen}
            offset={offset}
            onOpenChange={onOpenChange}
            placement={placement}
            popoverClassName={popoverClassName}
            style={popoverStyle}
            triggerRef={triggerRef}
        >
            {content}
        </BasePopover>
    );
}

/** @public */
export function MenuSeparator() {
    return <AriaSeparator className={styles.separator} />;
}

/** @public */
export type MenuSectionProps<T extends object = object> = {
    className?: string;
} & AriaSectionProps<T>;

/** @public */
export function MenuSection<T extends object = object>(props: MenuSectionProps<T>) {
    const { className, ...rest } = props;
    return <AriaMenuSection className={cx(styles.menuSection, className)} {...rest} />;
}

/** @public */
export function MenuSectionHeader({ children, className }: { children: React.ReactNode; className?: string }) {
    return <AriaHeader className={cx(styles.menuSectionHeader, className)}>{children}</AriaHeader>;
}

export type MenuItemButtonProps = {
    icon?: React.ComponentType<{ size?: number }>;
    text: string;
    testID?: string;
    destructive?: boolean;
    className?: string;
} & Omit<AriaMenuItemProps, 'children'>;

export function MenuItemButton({ icon: Icon, text, testID, destructive = false, className, ...rest }: MenuItemButtonProps) {
    return (
        <AriaMenuItem
            className={cx(styles.menuItemButton, 'extendedClickArea', destructive && styles.destructive, className)}
            data-testid={makeTestID({ testID, text })}
            textValue={text}
            {...rest}
        >
            {Icon && <Icon size={16} />}
            <span>{text}</span>
        </AriaMenuItem>
    );
}
